import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const languages = [
    { code: 'es', name: 'ES', flag: '🇪🇸' },
    { code: 'en', name: 'EN', flag: '🇬🇧' },
    { code: 'fr', name: 'FR', flag: '🇫🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setShowLangMenu(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const shouldBeScrolled = window.scrollY > 20;
      setIsScrolled(shouldBeScrolled);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty deps is correct - we only want this to run once on mount

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLangMenu && !event.target.closest('.language-selector')) {
        setShowLangMenu(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showLangMenu]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: t('nav.home'), id: 'hero' },
    { label: t('nav.about'), id: 'about' },
    { label: t('nav.applications'), id: 'applications' },
    { label: t('nav.products'), id: 'catalog' },
    { label: t('nav.contact'), id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* Top bar with contact info */}
        <div className="hidden md:flex items-center justify-end py-2 border-b border-gray-200 text-sm">
          <a href="tel:+34948670255" className="flex items-center gap-2 text-gray-600 hover:text-cmv-green transition-colors mr-6">
            <Phone size={14} />
            <span>+34 948 670 255</span>
          </a>
          <a href="mailto:info@cmvonline.com" className="flex items-center gap-2 text-gray-600 hover:text-cmv-green transition-colors">
            <Mail size={14} />
            <span>info@cmvonline.com</span>
          </a>
        </div>

        {/* Main navigation */}
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <div className="flex items-center">
            <button onClick={() => scrollToSection('hero')} className="focus:outline-none">
              <img 
                src="https://customer-assets.emergentagent.com/job_navarra-supply/artifacts/qn3t6vcs_logo_cmv-removebg-preview.png"
                alt="CMV"
                className="h-10 md:h-12 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 hover:text-cmv-green font-medium transition-colors relative group"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cmv-green transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
            
            {/* Language Selector */}
            <div className="relative language-selector">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 text-gray-700 hover:text-cmv-green font-medium transition-colors px-3 py-2 rounded-lg hover:bg-gray-100"
              >
                <Globe size={18} />
                <span>{currentLanguage.flag} {currentLanguage.name}</span>
              </button>
              
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2 ${
                        i18n.language === lang.code ? 'bg-gray-50 text-cmv-green font-semibold' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-cmv-green hover:bg-cmv-green-dark text-white px-6"
            >
              {t('hero.cta')}
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-cmv-green transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top-5 duration-300">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left py-3 text-gray-700 hover:text-cmv-green hover:bg-gray-50 px-4 transition-colors font-medium"
              >
                {item.label}
              </button>
            ))}
            
            {/* Language Selector for Mobile */}
            <div className="px-4 pt-4 pb-2 border-t border-gray-200 mt-2">
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">{t('nav.language') || 'Idioma / Language'}</p>
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-all ${
                      i18n.language === lang.code 
                        ? 'border-cmv-green bg-cmv-green/10 text-cmv-green font-semibold' 
                        : 'border-gray-200 text-gray-700 hover:border-cmv-green hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="text-sm font-medium">{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="px-4 pt-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="w-full bg-cmv-green hover:bg-cmv-green-dark text-white"
              >
                {t('hero.cta')}
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
